import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import type { ProductInquiry, ProductInquiryType } from '~/api/model';
import { INQUIRY_QUERY_KEY } from '~/api/query-keys';

import { inquiryRepository } from '../repository';

// GET /api/market-place/v1/product-inquiries
export const useInfiniteMyInquiries = () => {
  const { queryKey, queryFn } = INQUIRY_QUERY_KEY.myList();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(queryKey, queryFn, {
      suspense: true,
      getNextPageParam: (lastPage) => {
        if (!lastPage.hasNextPage) return undefined;
        return lastPage.lastId as number;
      },
    });

  const pages = data?.pages?.reduce<ProductInquiry[]>(
    (acc, page) => acc.concat(page.content),
    [],
  );

  return {
    pages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

// GET /api/market-place/v1/product-inquiries/count
export const useMyInquiryCount = () => {
  const { queryKey, queryFn } = INQUIRY_QUERY_KEY.count();

  const { data } = useQuery(queryKey, queryFn, {
    suspense: true,
  });

  return data ?? 0;
};

type EditInquiryParams = {
  inquiryId: number;
  requestBody: {
    offerId: number;
    content: string;
    isSecret: boolean;
    type: ProductInquiryType;
  };
};

// PUT /api/market-place/v1/product-inquiries/{inquiryId}
export const useEditProductInquiry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ inquiryId, requestBody }: EditInquiryParams) =>
      inquiryRepository.editInquiry(inquiryId, requestBody),
    onSuccess: () => {
      void queryClient.invalidateQueries(INQUIRY_QUERY_KEY.all);
    },
  });
};

// DELETE /api/market-place/v1/product-inquiries/{inquiryId}
export const useDeleteProductInquiry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inquiryId: number) =>
      inquiryRepository.deleteInquiry(inquiryId),
    onSuccess: () => {
      void queryClient.invalidateQueries(INQUIRY_QUERY_KEY.all);
    },
  });
};
